import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API, useAuth } from "@/auth";
import { sfx, confetti } from "@/lib/effects";
import BattleStage from "@/components/BattleStage";
import ShareBattleButton from "@/components/ShareBattleButton";

const SPIT_SECONDS = 180;
const TRANSITION_SECONDS = 15;
const COMMENTARY_PHRASES = [
 "LADIES AND GENTLEMEN — WE ARE LIVE FROM THE PITCH!",
 "OHHH! THE CROWD IS ELECTRIC TONIGHT!",
 "HERE WE GO FOLKS — TWO HEAVYWEIGHTS FACE TO FACE!",
 "THE STADIUM IS ON ITS FEET!",
 "WHAT A NIGHT! WHAT AN ATMOSPHERE!",
 "SIT DOWN, BUCKLE UP — THIS IS CINEMA!",
 "THE JUDGE IS LOCKED IN — SCORECARDS READY!",
 "STAY LOCKED IN — HISTORY IS ABOUT TO HAPPEN!",
];

/* ============================================================
 NEW BATTLE — Arena + Opponent + Rules (3 steps)
 ============================================================ */
export function NewBattle() {
 const { user } = useAuth();
 const nav = useNavigate();
 const [arenas, setArenas] = useState([]);
 const [roster, setRoster] = useState([]);
 const [arena, setArena] = useState(null);
 const [pick, setPick] = useState(null);
 const [rounds, setRounds] = useState(3);
 const [difficulty, setDifficulty] = useState("normal"); // easy | normal | hard
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");
 const [step, setStep] = useState(1);

 useEffect(() => {
 Promise.all([
 axios.get(`${API}/arenas`).then(r => setArenas(r.data)),
 axios.get(`${API}/battles/ai-roster`).then(r => setRoster(r.data)),
 ]).catch(() => {});
 }, []);

 const start = async () => {
 if (!pick || !arena) return;
 setBusy(true); setErr("");
 try {
 const { data } = await axios.post(`${API}/battles`, {
 ai_opponent_key: pick.key, arena_key: arena.key, rounds, difficulty,
 });
 nav(`/battles/${data.id}`);
 } catch (e) { setErr(e.response?.data?.detail || "Couldn't start battle"); }
 finally { setBusy(false); }
 };

 if (!user) return <div className="p-10 text-center"><Link to="/login" className="text-yt-yellow underline">Sign in to battle</Link></div>;

 // AVATAR-FIRST GATE — a user without a Character Studio portrait
 // gets a placeholder square on the battle stage, which looks amateur.
 // Force them into Studio first with a hard banner they can't skip.
 // Admins bypass so they can smoke-test flows.
 const hasPortrait = !!user.portrait;
 if (!hasPortrait && user.role !== "admin") {
   return (
     <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
       <div className="border-4 border-yt-yellow bg-yt-surface shadow-brutal p-8 md:p-10">
         <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-4">STEP ONE · BUILD YOUR CHARACTER</div>
         <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4 leading-none">
           NO CHARACTER, <span className="text-yt-red">NO BATTLE</span>.
         </h1>
         <p className="font-body text-white/80 mb-6">
           Every rapper on this stage has a face. Head over to Character Studio and
           generate your full-body portrait first — takes about 15 seconds. Then
           come back here and pick your fight.
         </p>
         <div className="flex gap-3 flex-wrap">
           <Link
             to="/studio"
             data-testid="battle-gate-studio"
             className="bg-yt-yellow text-black font-display text-lg tracking-widest px-6 py-3 shadow-brutal hover:bg-white"
           >
             ▶ BUILD MY CHARACTER
           </Link>
           <Link
             to="/how-it-works"
             data-testid="battle-gate-how"
             className="border-2 border-white text-white font-display text-lg tracking-widest px-6 py-3 hover:bg-white hover:text-black"
           >
             HOW IT WORKS
           </Link>
         </div>
       </div>
     </div>
   );
 }

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 {/* ============================================================
 PvP TAB — challenge a live human rapper instead of an AI.
 Sits ABOVE the AI matchmaker so it's the first thing new
 subscribers see when hunting for a fight. Links to /rappers
 where the roster + challenge form live.
 ============================================================ */}
 <Link
 to="/rappers"
 data-testid="pvp-cta-banner"
 className="block bg-yt-red border-4 border-yt-yellow p-5 md:p-6 mb-8 shadow-brutal hover:bg-black hover:text-white transition-colors group"
 >
 <div className="flex items-center justify-between gap-4 flex-wrap">
 <div>
 <div className="font-mono text-[10px] tracking-[0.4em] text-black group-hover:text-yt-yellow mb-1"> PVP · MULTIPLAYER MODE</div>
 <div className="font-display text-2xl md:text-4xl tracking-tight text-white leading-tight">
 CHALLENGE A <span className="text-yt-yellow">LIVE RAPPER</span>
 </div>
 <div className="font-mono text-xs text-white/90 mt-1">Send a verse. Wait 48h. Judge DeGagne decides. Points to the winner.</div>
 </div>
 <div className="bg-black text-yt-yellow font-display text-lg md:text-2xl tracking-widest px-5 py-3 border-2 border-yt-yellow group-hover:bg-yt-yellow group-hover:text-black transition-colors">
 BROWSE ROSTER ▶
 </div>
 </div>
 </Link>

 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-2"> AI MATCHMAKE · STEP {step}/3</div>
 <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-2">
 {step === 1 && <>PICK THE <span className="text-yt-red">ARENA</span></>}
 {step === 2 && <>CHOOSE YOUR <span className="text-yt-red">ENEMY</span></>}
 {step === 3 && <>SET THE <span className="text-yt-red">RULES</span></>}
 </h1>
 <p className="text-yt-muted mb-4 font-mono text-xs tracking-widest">
 BATTLES REMAINING: <span className="text-yt-yellow">{user.battles_remaining}</span> · TIER: <span className="text-yt-yellow">{user.tier?.toUpperCase()}</span>
 </p>

 {step === 1 && (
 <>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
 {arenas.map(a => (
 <button key={a.key} data-testid={`arena-${a.key}`} onClick={() => setArena(a)}
 className={`relative overflow-hidden bg-yt-surface border text-left transition-all group ${arena?.key === a.key ? "border-yt-yellow shadow-brutal-yellow" : "border-yt-border hover:border-yt-yellow"}`}>
 <div className="aspect-[16/9] bg-black overflow-hidden">
 <img src={a.image} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
 </div>
 <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
 <div className="absolute bottom-0 left-0 right-0 p-5">
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-yellow">{a.key.toUpperCase()}</div>
 <div className="font-display text-3xl tracking-tight">{a.name}</div>
 <div className="font-mono text-xs text-yt-muted mt-1">{a.subtitle}</div>
 </div>
 {arena?.key === a.key && <div className="absolute top-3 right-3 bg-yt-yellow text-black font-mono text-[10px] tracking-widest px-2 py-1">✓ SELECTED</div>}
 </button>
 ))}
 </div>
 <div className="flex justify-end mt-8">
 <button data-testid="step-1-next" disabled={!arena} onClick={() => setStep(2)} className="bg-yt-yellow text-black font-display text-lg tracking-wider px-8 py-3 shadow-brutal disabled:opacity-40">NEXT ▶</button>
 </div>
 </>
 )}

 {step === 2 && (
 <>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
 {roster.map(ai => (
 <button key={ai.key} data-testid={`opponent-${ai.key}`} onClick={() => setPick(ai)}
 className={`bg-yt-surface border text-left transition-all overflow-hidden ${pick?.key === ai.key ? "border-yt-yellow shadow-brutal-yellow" : "border-yt-border hover:border-yt-yellow"}`}>
 <div className="aspect-square bg-black overflow-hidden relative">
 {ai.portrait ? <img src={ai.portrait} alt={ai.name} className="w-full h-full object-cover" /> :
 <div className="w-full h-full flex items-center justify-center font-display text-5xl text-yt-yellow bg-gradient-to-br from-yt-surface to-black">{(ai.name || "??").slice(0, 2).toUpperCase()}</div>}
 <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
 </div>
 <div className="p-4">
 <div className="font-display text-xl tracking-tight">{ai.name}</div>
 <div className="text-[10px] text-yt-muted font-mono">{ai.style.toUpperCase()}</div>
 <div className="mt-3 h-1 bg-black overflow-hidden"><div className="h-full bg-yt-yellow" style={{ width: `${ai.skill * 100}%` }} /></div>
 <div className="text-[10px] font-mono text-yt-muted mt-1">SKILL · {Math.round(ai.skill * 100)}</div>
 </div>
 </button>
 ))}
 </div>
 <div className="flex justify-between mt-8">
 <button onClick={() => setStep(1)} className="border border-yt-border px-6 py-3 font-display tracking-wider hover:border-yt-yellow">◀ BACK</button>
 <button data-testid="step-2-next" disabled={!pick} onClick={() => setStep(3)} className="bg-yt-yellow text-black font-display text-lg tracking-wider px-8 py-3 shadow-brutal disabled:opacity-40">NEXT ▶</button>
 </div>
 </>
 )}

 {step === 3 && (
 <>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
 <PreviewCard label="ARENA" img={arena.image} title={arena.name} />
 <PreviewCard label="YOU" img={user.portrait} title={user.rapper_name} subtitle={user.style?.toUpperCase()} />
 <PreviewCard label="OPPONENT" img={pick.portrait} title={pick.name} subtitle={pick.style?.toUpperCase()} />
 </div>
 <div className="mt-10">
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted mb-2">DIFFICULTY</div>
 <div className="flex gap-2 flex-wrap">
 {[
 {k:"easy", label:"EASY", sub:"Weaker AI · warm-up"},
 {k:"normal", label:"NORMAL", sub:"Solid vet · balanced"},
 {k:"hard", label:"HARD", sub:"Merciless · headliner"},
 ].map(d => (
 <button
 key={d.k}
 data-testid={`difficulty-${d.k}`}
 onClick={() => setDifficulty(d.k)}
 className={`px-5 py-3 font-display text-lg border-2 text-left leading-tight ${
 difficulty === d.k
 ? "bg-yt-yellow text-black border-yt-yellow shadow-brutal"
 : "border-yt-border text-white hover:border-yt-yellow"
 }`}>
 {d.label}
 <div className={`font-mono text-[9px] tracking-widest mt-1 ${difficulty===d.k?"text-black/70":"text-yt-muted"}`}>{d.sub}</div>
 </button>
 ))}
 </div>
 </div>
 <div className="mt-8">
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted mb-2">ROUNDS</div>
 <div className="flex gap-2">
 {[3, 5].map(n => (
 <button key={n} data-testid={`rounds-${n}`} onClick={() => setRounds(n)}
 className={`px-6 py-3 font-display text-2xl border ${rounds === n ? "bg-yt-yellow text-black border-yt-yellow" : "border-yt-border text-white hover:border-yt-yellow"}`}>{n}</button>
 ))}
 </div>
 </div>
 <div className="flex justify-between mt-10">
 <button onClick={() => setStep(2)} className="border border-yt-border px-6 py-3 font-display tracking-wider hover:border-yt-yellow">◀ BACK</button>
 <button data-testid="start-battle-btn" onClick={start} disabled={busy} className="bg-yt-red text-white font-display text-2xl tracking-wider px-12 py-4 hover:bg-yt-yellow hover:text-black shadow-brutal shadow-brutal-hover disabled:opacity-40">
 {busy ? "ENTERING..." : "▶ ENTER THE ARENA"}
 </button>
 </div>
 {err && <div data-testid="new-battle-error" className="mt-6 font-mono text-xs text-yt-red border border-yt-red/40 bg-yt-red/5 p-3">{err}</div>}
 </>
 )}
 </div>
 );
}

function PreviewCard({ label, img, title, subtitle }) {
 return (
 <div className="bg-yt-surface border border-yt-border overflow-hidden">
 <div className="aspect-video bg-black overflow-hidden flex items-center justify-center">
 {img ? <img src={img} alt={title} className="w-full h-full object-cover" /> : <div className="font-display text-5xl text-yt-yellow">{(title || "??").slice(0, 2).toUpperCase()}</div>}
 </div>
 <div className="p-4">
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted">{label}</div>
 <div className="font-display text-xl tracking-tight">{title}</div>
 {subtitle && <div className="text-[10px] text-yt-muted font-mono">{subtitle}</div>}
 </div>
 </div>
 );
}

/* ============================================================
 HOOKS
 ============================================================ */
function useCountdown(seconds, active, onExpire) {
 const [left, setLeft] = useState(seconds);
 useEffect(() => { setLeft(seconds); }, [seconds, active]);
 useEffect(() => {
 if (!active) return;
 if (left <= 0) { onExpire?.(); return; }
 const t = setTimeout(() => setLeft(l => l - 1), 1000);
 return () => clearTimeout(t);
 }, [left, active]);
 return left;
}

/**
 * Whisper-based mic recorder. Records audio via MediaRecorder → uploads to /api/voice/transcribe
 * Result appended via onAppend callback.
 */
function useWhisperMic(onAppend) {
 const recRef = useRef(null);
 const chunksRef = useRef([]);
 const streamRef = useRef(null);
 const [state, setState] = useState("idle"); // idle | recording | transcribing
 const [status, setStatus] = useState("");
 const [supported] = useState(() =>
 typeof window !== "undefined" && !!navigator.mediaDevices && !!window.MediaRecorder,
 );

 const stop = useCallback(() => {
 try { recRef.current?.stop(); } catch { /* noop */ }
 try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch { /* noop */ }
 }, []);

 const toggle = async () => {
 if (!supported) { setStatus("Mic recording not supported in this browser. Try Chrome."); return; }
 if (state === "recording") { stop(); return; }
 if (state === "transcribing") return;

 setStatus("Requesting mic access...");
 try {
 const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
 streamRef.current = stream;
 // Prefer webm/opus (Whisper supports it directly)
 let mime = "audio/webm;codecs=opus";
 if (!window.MediaRecorder.isTypeSupported(mime)) mime = "audio/webm";
 if (!window.MediaRecorder.isTypeSupported(mime)) mime = "";
 const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
 recRef.current = rec;
 chunksRef.current = [];
 rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
 rec.onstop = async () => {
 try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch { /* noop */ }
 const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
 if (!blob.size) { setState("idle"); setStatus("Nothing captured — try again."); return; }
 setState("transcribing");
 setStatus("Whisper listening... transcribing your bars");
 try {
 const fd = new FormData();
 fd.append("file", blob, "spit.webm");
 const r = await axios.post(`${API}/voice/transcribe`, fd, { timeout: 60000 });
 const txt = (r.data.text || "").trim();
 if (txt) {
 onAppend(txt);
 setStatus(`✓ Captured: "${txt.slice(0, 80)}"`);
 } else {
 setStatus("Whisper heard silence. Speak up and try again.");
 }
 } catch (e) {
 setStatus("⚠ Whisper failed: " + (e.response?.data?.detail || e.message));
 } finally {
 setState("idle");
 }
 };
 rec.start();
 setState("recording");
 setStatus("REC · tap again when you're done spitting");
 } catch (e) {
 setState("idle");
 setStatus("Mic blocked or unavailable: " + (e.message || e));
 }
 };

 useEffect(() => () => { stop(); }, [stop]);

 return { state, status, supported, toggle };
}

/* ============================================================
 BROADCAST-STYLE BATTLE ROOM
 ============================================================ */
export function BattleRoom() {
 const { id } = useParams();
 const { user } = useAuth();
 const [battle, setBattle] = useState(null);
 const [verse, setVerse] = useState("");
 // phases: intro | spitting | gen_user | play_user | gen_opp | play_opp | judge_speak | reveal | done
 const [phase, setPhase] = useState("intro");
 const [err, setErr] = useState("");
 const [commentaryIdx, setCommentaryIdx] = useState(0);
 const [typedVerdict, setTypedVerdict] = useState("");
 const [audioUserUrl, setAudioUserUrl] = useState(null);
 const [audioOppUrl, setAudioOppUrl] = useState(null);
 const [judgeAudioUrl, setJudgeAudioUrl] = useState(null);
 const [audioStatus, setAudioStatus] = useState("");
 const [genProgress, setGenProgress] = useState(0);

 // Refs so async chains don't stomp on stale closure state
 const battleRef = useRef(null);
 const oppSunoPromiseRef = useRef(null);
 const battlePromiseRef = useRef(null);

 const spitLeft = useCountdown(SPIT_SECONDS, phase === "spitting", () => { if (verse.length >= 10) submit(); });
 const introLeft = useCountdown(TRANSITION_SECONDS, phase === "intro", () => setPhase("spitting"));
 const mic = useWhisperMic((t) => setVerse(v => (v ? v + "\n" : "") + t));

 const load = async () => {
 try {
 const { data } = await axios.get(`${API}/battles/${id}`);
 setBattle(data);
 battleRef.current = data;
 if (data.status === "completed" || data.status === "abandoned") setPhase("done");
 else setPhase("intro");
 } catch { setErr("Battle not found"); }
 };
 useEffect(() => { load(); }, [id]);

 // Rotate judge commentary during long-running phases
 useEffect(() => {
 if (!["gen_user", "gen_opp"].includes(phase)) return;
 const it = setInterval(() => setCommentaryIdx(i => (i + 1) % COMMENTARY_PHRASES.length), 3500);
 return () => clearInterval(it);
 }, [phase]);

 // Fake progress bar during gen phases
 useEffect(() => {
 if (!["gen_user", "gen_opp"].includes(phase)) { setGenProgress(0); return; }
 setGenProgress(0);
 const started = Date.now();
 const it = setInterval(() => {
 const elapsed = (Date.now() - started) / 1000;
 // asymptote toward 92% over ~2 min
 const pct = Math.min(92, (1 - Math.exp(-elapsed / 45)) * 100);
 setGenProgress(pct);
 }, 500);
 return () => clearInterval(it);
 }, [phase]);

 // Type verdict during reveal
 useEffect(() => {
 if (phase !== "reveal") return;
 const b = battleRef.current || battle;
 const lastRound = b?.rounds?.[b.rounds.length - 1];
 const text = lastRound?.judge_user?.verdict || "";
 setTypedVerdict("");
 let i = 0;
 const it = setInterval(() => {
 i++; setTypedVerdict(text.slice(0, i));
 if (i >= text.length) clearInterval(it);
 }, 35);
 return () => clearInterval(it);
 }, [phase, battle]);

 // Auto-advance from reveal → done/intro after judge finishes
 useEffect(() => {
 if (phase !== "reveal") return;
 const b = battleRef.current || battle;
 const t = setTimeout(() => {
 if (b?.status === "completed") {
 setPhase("done");
 if (b.winner === "user") { sfx.victory(); confetti(); }
 else if (b.winner === "opponent") { sfx.defeat(); }
 } else {
 setPhase("intro");
 }
 }, 14000);
 return () => clearTimeout(t);
 }, [phase, battle]);

 // Poll the battle every 10s during reveal/done so the lipsync MP4s can
 // arrive and the BattleStage swaps portraits → video ("head-to-head" feel).
 useEffect(() => {
 if (phase !== "reveal" && phase !== "done") return;
 let cancelled = false;
 const tick = async () => {
 try {
 const { data } = await axios.get(`${API}/battles/${id}`);
 if (cancelled) return;
 const lr = data?.rounds?.[data.rounds.length - 1];
 const hasLip = lr?.lipsync_user || lr?.lipsync_opponent;
 if (hasLip) {
 setBattle(data);
 battleRef.current = data;
 }
 } catch { /* transient */ }
 };
 tick(); // immediate
 const it = setInterval(tick, 10000);
 return () => { cancelled = true; clearInterval(it); };
 }, [phase, id]);

 // Poll Suno for a task_id until audio_url is playable
 const pollSuno = async (taskId, tag = "") => {
 for (let i = 0; i < 24; i++) { // up to ~4 min
 await new Promise(r => setTimeout(r, 7500));
 try {
 const s = await axios.get(`${API}/voice/status/${taskId}`);
 if (s.data.audio_url) return s.data.audio_url;
 const st = s.data.status;
 if (st === "CREATE_TASK_FAILED" || st === "GENERATE_AUDIO_FAILED") {
 setAudioStatus(`⚠ Suno ${tag} failed: ${st}`);
 return null;
 }
 } catch { /* transient */ }
 }
 setAudioStatus(`⚠ Suno ${tag} timeout after ~3 min`);
 return null;
 };

 // Kick off a Suno generation and resolve the audio URL (or null)
 const startSuno = async (lyrics, styleRaw, tag, gender = "male") => {
 const style = (styleRaw || "default").toLowerCase().replace(/ /g, "_");
 try {
 const r = await axios.post(`${API}/voice/generate`, { lyrics, style, gender }, { timeout: 30000 });
 if (r.data.audio_url) return r.data.audio_url; // cache hit
 if (r.data.task_id) return await pollSuno(r.data.task_id, tag);
 return null;
 } catch (e) {
 setAudioStatus("⚠ " + (e.response?.data?.detail || e.message));
 return null;
 }
 };

 // TTS fallback for a verse — used when Suno fails or times out so the
 // opponent (or user) ALWAYS spits their bars audibly. Returns a base64
 // data URL playable by <audio>. Passes opponent_key so the backend
 // picks the CHARACTER-SPECIFIC voice (never onyx, which is Judge only).
 const startVerseTTS = async (lyrics, gender = "male", opponentKey = null) => {
 try {
 const r = await axios.post(`${API}/voice/verse-tts`,
 { text: lyrics, gender, opponent_key: opponentKey }, { timeout: 60000 });
 if (r.data?.audio_b64) {
 return `data:${r.data.mime || "audio/mpeg"};base64,${r.data.audio_b64}`;
 }
 } catch (e) {
 console.warn("verse-tts fallback failed", e);
 }
 return null;
 };

 // Fire finalize-round once both audios are ready — kicks off backend lipsync
 // generation for this round so the "done" screen can show head-to-head
 // face-animated videos of the rappers spitting.
 const finalizeRoundLipsync = async (roundNum, userUrl, oppUrl) => {
 // Skip local data-URLs (TTS base64) — Replicate needs a public HTTP URL.
 const userOk = userUrl && !userUrl.startsWith("data:") ? userUrl : null;
 const oppOk = oppUrl && !oppUrl.startsWith("data:") ? oppUrl : null;
 if (!userOk && !oppOk) return;
 try {
 await axios.post(`${API}/battles/${id}/finalize-round`, {
 round: roundNum,
 user_audio_url: userOk,
 opponent_audio_url: oppOk,
 }, { timeout: 15000 });
 } catch (e) {
 console.warn("finalize-round failed", e);
 }
 };

 // Poll the battle doc until the lipsync MP4 for a given round + side lands.
 // User wants BOTH audio AND lipsync to be ready before playback fires so
 // face + voice sync perfectly. Timeout after ~3 min → fallback to plain audio.
 // Returns the lipsync MP4 URL when ready, or null on timeout.
 const waitForLipsyncReady = async (roundNum, side, maxWaitMs = 180000) => {
 const key = side === "user" ? "lipsync_user" : "lipsync_opponent";
 const start = Date.now();
 while (Date.now() - start < maxWaitMs) {
 try {
 const { data } = await axios.get(`${API}/battles/${id}`);
 const r = data?.rounds?.find(x => x.round === roundNum);
 if (r?.[key]) {
 // Merge into local state so BattleStage sees the new lipsync path
 setBattle(data);
 battleRef.current = data;
 return r[key];
 }
 } catch { /* transient */ }
 await new Promise(res => setTimeout(res, 4000));
 }
 return null;
 };

 // Fetch judge TTS audio
 const generateJudgeAudio = async (text) => {
 try {
 const r = await axios.post(`${API}/voice/judge-speak`, { text, voice: "onyx" }, { timeout: 60000 });
 if (r.data?.audio_b64) {
 return `data:${r.data.mime || "audio/mpeg"};base64,${r.data.audio_b64}`;
 }
 } catch (e) {
 console.warn("judge TTS failed", e);
 }
 return null;
 };

 // Live commentary — Judge DeGagne fills the dead air while Suno stitches the next verse.
 // Fetches a fresh 1-2 sentence take + TTS, drops it into the running commentaryAudio slot.
 const [commentaryText, setCommentaryText] = useState("");
 const commentaryAudioRef = useRef(null);
 const phaseRef = useRef(phase);
 useEffect(() => { phaseRef.current = phase; }, [phase]);

 const fetchJudgeCommentary = async (which, opts = {}) => {
 try {
 const r = await axios.post(`${API}/voice/judge-commentary`, {
 phase: which, // "intro" | "reaction" | "stall"
 rapper_name: opts.rapperName,
 opponent_name: opts.opponentName,
 previous_verse: opts.previousVerse,
 arena_name: opts.arenaName,
 }, { timeout: 60000 });
 if (r.data?.text) setCommentaryText(r.data.text);
 if (r.data?.audio_b64) {
 // CRITICAL: Only play commentary audio during dead-air gen phases.
 // If phase moved to play_user / play_opp / reveal / done while we were
 // awaiting the API, DON'T start the audio — it would overlap the rap.
 const currentPhase = phaseRef.current;
 if (!["gen_user", "gen_opp", "gen_user_lipsync", "gen_opp_lipsync"].includes(currentPhase)) {
 return; // Rap or verdict is now playing; skip judge audio
 }
 const url = `data:${r.data.mime || "audio/mpeg"};base64,${r.data.audio_b64}`;
 if (commentaryAudioRef.current) {
 try { commentaryAudioRef.current.pause(); } catch { /* noop */ }
 try { commentaryAudioRef.current.remove(); } catch { /* noop */ }
 }
 // CRITICAL: attach the <audio> to the DOM so the global audio-gate
 // (installAudioGate in /app/frontend/src/lib/audio-gate.js) can see
 // it. Detached `new Audio()` objects don't participate in
 // document-level `play` events, which caused judge commentary to
 // play on top of a rapper's verse ("both rappers at the same time"
 // — the user has reported this across 3 forks).
 const a = document.createElement("audio");
 a.src = url;
 a.volume = 0.9;
 a.setAttribute("data-audio-role", "judge-commentary");
 a.style.display = "none";
 document.body.appendChild(a);
 const cleanup = () => { try { a.remove(); } catch { /* noop */ } };
 a.addEventListener("ended", cleanup);
 a.addEventListener("error", cleanup);
 commentaryAudioRef.current = a;
 try { await a.play(); } catch { /* autoplay policies may block, silent OK */ }
 }
 } catch (e) {
 console.warn("judge commentary failed", e);
 }
 };
 // Stop any playing commentary when leaving gen phases
 useEffect(() => {
 if (!["gen_user", "gen_opp", "gen_user_lipsync", "gen_opp_lipsync"].includes(phase)) {
 if (commentaryAudioRef.current) {
 try { commentaryAudioRef.current.pause(); } catch { /* noop */ }
 try { commentaryAudioRef.current.remove(); } catch { /* noop */ }
 commentaryAudioRef.current = null;
 }
 }
 }, [phase]);

 const submit = async () => {
 if (verse.length < 10) { setErr("Drop at least a few bars before submitting."); return; }
 if (mic.state === "recording") mic.toggle();

 setErr("");
 setAudioUserUrl(null); setAudioOppUrl(null); setJudgeAudioUrl(null);
 setAudioStatus("Suno is cooking your bars — this takes 30-90 sec.");
 setPhase("gen_user");
 sfx.drop();
 // Judge DeGagne hypes the incoming verse while the beat is stitching
 fetchJudgeCommentary("intro", {
 rapperName: user.rapper_name,
 opponentName: battle?.opponent_name,
 arenaName: battle?.arena_name,
 });

 const userLyrics = verse;
 const userStyle = user.style || "default";

 // 1) Kick off Suno for USER's verse (blocking wait)
 const userGender = user.gender || "male";
 const userSunoPromise = startSuno(userLyrics, userStyle, "user", userGender);

 // 2) In parallel, submit the verse to the backend to get opponent's verse + judging
 const battlePromise = axios
 .post(`${API}/battles/${id}/submit-verse`, { text: verse }, { timeout: 180000 })
 .then(r => r.data)
 .catch(e => ({ __error: e }));
 battlePromiseRef.current = battlePromise;

 // Wait for USER audio first
 let userAudio = await userSunoPromise;
 if (!userAudio) {
 // TTS fallback for user's own bars too — so their spit is heard even
 // if Suno chokes on their session.
 setAudioStatus("⚠ Suno couldn't cook your voice — using TTS fallback so you're heard...");
 userAudio = await startVerseTTS(userLyrics, userGender);
 }
 if (userAudio) {
 // WAIT for lipsync + audio to BOTH be ready before firing playback.
 // Ensures the animated face + voice hit at the same instant.
 setAudioStatus("Syncing your face to the bars — hold up 30-90 sec (both fire together)");
 setPhase("gen_user_lipsync");
 // Fire lipsync gen immediately for the current round (backend will
 // fill in the mp4 URL on the round doc when Replicate returns)
 // Round num is not yet known at this point (backend hasn't responded)
 // so we defer lipsync trigger until battlePromise resolves below.
 const bd = await battlePromise;
 if (bd && !bd.__error) {
 setBattle(bd); battleRef.current = bd; setVerse("");
 const lastRound = bd.rounds[bd.rounds.length - 1];
 // Kick off lipsync (user side) NOW so it's cooking while we wait
 if (userAudio && !userAudio.startsWith("data:")) {
 await finalizeRoundLipsync(lastRound.round, userAudio, null);
 // Wait for user's lipsync MP4 to be ready (or timeout → fallback audio)
 const lipUrl = await waitForLipsyncReady(lastRound.round, "user");
 if (lipUrl) {
 setAudioStatus("▶ Playing your bars with lipsync...");
 } else {
 setAudioStatus("▶ Playing your bars (lipsync took too long — audio-only)...");
 }
 }
 setAudioUserUrl(userAudio);
 setPhase("play_user");
 // Fetch judge audio + start opponent Suno in parallel now that we have bd
 if (lastRound?.opponent_verse) {
 oppSunoPromiseRef.current = startSuno(lastRound.opponent_verse, bd.opponent_style, "opp", bd.opponent_gender || "male");
 }
 if (lastRound?.judge_user?.verdict) {
 generateJudgeAudio(lastRound.judge_user.verdict).then(url => { if (url) setJudgeAudioUrl(url); });
 }
 } else if (bd?.__error) {
 setErr(bd.__error.response?.data?.detail || "Judge failed to score the round.");
 return;
 }
 } else {
 setAudioStatus("⚠ Voice generation unavailable — moving on to opponent.");
 }

 // If user audio never came, immediately advance to opponent-gen phase
 if (!userAudio) {
 await new Promise(r => setTimeout(r, 1200));
 await advanceToOpponent();
 }
 // Otherwise: audio.onEnded handler on the <audio> triggers advanceToOpponent()
 };

 // After user's audio finishes → generate + play opponent's audio → verdict
 const advanceToOpponent = async () => {
 // Ensure we have battle data
 let bd = battleRef.current;
 if (!bd || !bd.rounds?.length || bd.rounds[bd.rounds.length - 1].round !== bd.current_round - 1) {
 // Battle data not arrived yet, wait for it
 const arrived = await battlePromiseRef.current;
 if (arrived && !arrived.__error) { bd = arrived; battleRef.current = arrived; setBattle(arrived); }
 }
 const lastRound = bd?.rounds?.[bd.rounds.length - 1];
 if (!lastRound?.opponent_verse) { revealVerdict(); return; }

 setPhase("gen_opp");
 setAudioStatus(` ${bd.opponent_name} is cooking a comeback...`);
 // Judge DeGagne reacts to the user's verse while opponent's track cooks
 fetchJudgeCommentary("reaction", {
 rapperName: bd.rapper_name,
 opponentName: bd.opponent_name,
 previousVerse: lastRound.user_verse,
 arenaName: bd.arena_name,
 });
 // Await opponent Suno (started in the background when battle data arrived).
 // If never started (edge case), start now.
 if (!oppSunoPromiseRef.current) {
 oppSunoPromiseRef.current = startSuno(lastRound.opponent_verse, bd.opponent_style, "opp", bd.opponent_gender || "male");
 }
 let oppAudio = await oppSunoPromiseRef.current;
 oppSunoPromiseRef.current = null;
 // CRITICAL FALLBACK: if Suno fails/times out, use OpenAI TTS so the
 // opponent ALWAYS spits their bars. Silent rebuttals = broken UX.
 if (!oppAudio) {
 setAudioStatus(`⚠ Suno failed for ${bd.opponent_name}. Falling back to TTS voice...`);
 oppAudio = await startVerseTTS(lastRound.opponent_verse, bd.opponent_gender || "male", bd.opponent_key);
 }
 if (oppAudio) {
 // Wait for opponent's lipsync + audio to BOTH be ready. Same UX as user side.
 setAudioStatus(` Syncing ${bd.opponent_name}'s face to their bars — hold up...`);
 setPhase("gen_opp_lipsync");
 // Kick off lipsync gen for opponent (with actual audio URL, not TTS data-URL)
 if (oppAudio && !oppAudio.startsWith("data:")) {
 await finalizeRoundLipsync(lastRound.round, null, oppAudio);
 const lipUrl = await waitForLipsyncReady(lastRound.round, "opp");
 if (lipUrl) {
 setAudioStatus(`▶ ${bd.opponent_name} clapping back with lipsync...`);
 } else {
 setAudioStatus(`▶ ${bd.opponent_name} clapping back (audio-only, lipsync timed out)...`);
 }
 }
 setAudioOppUrl(oppAudio);
 setPhase("play_opp");
 // audio.onEnded → revealVerdict()
 } else {
 // Even TTS failed — show the verse text prominently and move on
 setAudioStatus(`⚠ Voice generation unavailable — read ${bd.opponent_name}'s bars below.`);
 setPhase("play_opp");
 // Auto-advance after 10s so user isn't stuck
 setTimeout(() => revealVerdict(), 10000);
 }
 };

 const revealVerdict = async () => {
 const bd = battleRef.current || battle;
 setPhase("judge_speak");
 sfx.gavel();
 // Ensure judge audio is available; wait briefly if not
 let ju = judgeAudioUrl;
 if (!ju) {
 const verdict = bd?.rounds?.[bd.rounds.length - 1]?.judge_user?.verdict;
 if (verdict) ju = await generateJudgeAudio(verdict);
 if (ju) setJudgeAudioUrl(ju);
 }
 // Move to reveal so the audio element renders and autoplays
 setPhase("reveal");
 };

 const forfeit = async () => {
 if (!confirm("Throw the towel? This counts as a loss.")) return;
 try {
 const { data } = await axios.post(`${API}/battles/${id}/forfeit`);
 setBattle(data); battleRef.current = data; setPhase("done");
 } catch (e) { setErr(e.response?.data?.detail || "Couldn't forfeit"); }
 };

 // Karaoke sync — same trick as Cypher.jsx: derive current lyric line from
 // the active audio element's currentTime/duration so the ticker stays glued
 // to the vocalist instead of racing ahead on a fixed interval. Declared
 // BEFORE any early return so hook order stays stable across renders.
 const [stageAudioTime, setStageAudioTime] = useState(0);
 const [stageAudioDur, setStageAudioDur] = useState(0);
 useEffect(() => { setStageAudioTime(0); setStageAudioDur(0); }, [phase]);

 if (!battle) return <div className="p-10 font-mono text-yt-muted">{err || "Loading battle..."}</div>;

 const mm = (s) => String(Math.floor(s/60)).padStart(2,"0") + ":" + String(s%60).padStart(2,"0");
 const lastRound = battle.rounds[battle.rounds.length - 1];
 const userRoundsWon = battle.score_user;
 const oppRoundsWon = battle.score_opponent;
 const totalRounds = battle.rounds_total;

 const inAudioPhase = ["gen_user", "gen_user_lipsync", "play_user", "gen_opp", "gen_opp_lipsync", "play_opp"].includes(phase);
 const inRevealPhase = phase === "reveal" || phase === "judge_speak";

 const stageLines = (
 phase === "play_user" && lastRound?.user_verse ? lastRound.user_verse.split("\n").filter(l => l.trim()) :
 phase === "play_opp" && lastRound?.opponent_verse ? lastRound.opponent_verse.split("\n").filter(l => l.trim()) :
 []
 );
 let stageLineIdx = 0;
 if (stageLines.length > 0 && stageAudioDur > 0 && isFinite(stageAudioDur)) {
 stageLineIdx = Math.min(stageLines.length - 1,
 Math.floor((stageAudioTime / stageAudioDur) * stageLines.length));
 }

 // Compute BattleStage props from current phase
 const timerValueLeft = mm(phase === "spitting" ? spitLeft : SPIT_SECONDS);
 const timerValueRight = mm(SPIT_SECONDS);
 const centerCue = (
 phase === "intro" ? `${introLeft} SEC` :
 phase === "gen_user" ? "SUNO COOKING" :
 phase === "gen_user_lipsync"? "SYNCING FACE + VOICE" :
 phase === "play_user" ? "YOU'RE UP" :
 phase === "gen_opp" ? "OPP LOADING" :
 phase === "gen_opp_lipsync" ? "SYNCING FACE + VOICE" :
 phase === "play_opp" ? "OPP RAPPING" :
 phase === "judge_speak" ? "VERDICT" :
 phase === "reveal" ? "VERDICT" :
 phase === "done" ? (battle.status === "abandoned" ? "CLOSED" : battle.winner === "user" ? "★ VICTORY" : battle.winner === "draw" ? "DRAW" : "DEFEATED") :
 null
 );
 const verseLineStage = (
 phase === "spitting" && verse ? verse.split("\n").slice(-1)[0] :
 (phase === "play_user" || phase === "play_opp") && stageLines.length > 0 ? stageLines[stageLineIdx] :
 null
 );
 const judgeCommentStage = (
 phase === "intro" ? `Round ${battle.current_round} · Rappers to your corners. 3 minutes per spit.` :
 phase === "spitting" ? `Analyzing draft live, ${battle.rapper_name}. Bars so far: ${verse.split(/\n/).filter(l => l.trim()).length}` :
 phase === "gen_user" || phase === "gen_opp" || phase === "gen_user_lipsync" || phase === "gen_opp_lipsync" ? (commentaryText || COMMENTARY_PHRASES[commentaryIdx]) :
 phase === "play_user" ? "Defendant is spitting. Recording bars for the court record." :
 phase === "play_opp" ? "Opposing counsel objects with fresh bars. Listening carefully." :
 phase === "judge_speak" ? "ORDER IN THE CYPHER! Verdict incoming..." :
 phase === "reveal" ? (typedVerdict || "") :
 phase === "done" ? (battle.status === "abandoned" ? "Left untouched for weeks. Closed by Neo." : battle.winner === "user" ? "Champion declared." : battle.winner === "draw" ? "Verdict: draw." : "Defendant... defeated.") :
 null
 );

 return (
 <div className="bg-black">
 {/* ============================================================
 BROADCAST STAGE — same UFC-style look as Cypher/PvP
 ============================================================ */}
 <BattleStage
 fullscreen={false}
 arenaImage={battle.arena_image}
 arenaVideo={battle.arena_video}
 arenaName={battle.arena_name}
 round={Math.min(battle.current_round, totalRounds)}
 totalRounds={totalRounds}
 centerCue={centerCue}
 timerLeft={timerValueLeft}
 timerRight={timerValueRight}
 fighterA={{
 name: battle.rapper_name,
 portrait: battle.rapper_portrait,
 lipsync: (phase === "play_user" || phase === "done" || phase === "reveal") ? lastRound?.lipsync_user : null,
 rapping: phase === "spitting" || phase === "play_user",
 }}
 fighterB={{
 name: battle.opponent_name,
 portrait: battle.opponent_portrait,
 motion: battle.opponent_motion,
 lipsync: (phase === "play_opp" || phase === "done" || phase === "reveal") ? lastRound?.lipsync_opponent : null,
 rapping: phase === "play_opp",
 isWinner: phase === "done" && battle.winner === "opponent",
 }}
 verseLine={verseLineStage}
 judgeComment={judgeCommentStage}
 purseLeft={`PURSE: $${battle.purse || 5}`}
 purseRight={phase !== "done" && !inAudioPhase ? "EXTRA ROUND: $2" : null}
 onThrowTowel={phase !== "done" && phase !== "reveal" && phase !== "judge_speak" ? forfeit : null}
 muted={true}
 onUnmute={null}
 extraTopRight={phase === "done" ? <ShareBattleButton compact /> : null}
 />

 {/* NOTE: user/opponent/judge Suno & TTS audio are all rendered by the
 visible playback cards below the stage. No hidden duplicate audio
 elements — that pattern caused the "two voices playing at once"
 bug reported by the user. If you need to add automated audio
 triggers, add them to the visible <audio> elements, don't clone. */}

 {/* ============================================================
 BELOW THE STAGE
 ============================================================ */}
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
 {phase === "intro" && (
 <div className="bg-yt-surface border border-yt-border p-8 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3"> ROUND {battle.current_round} STARTING</div>
 <div className="font-display text-5xl md:text-7xl tracking-tight mb-4">GET READY TO SPIT</div>
 <p className="text-yt-muted mb-6 max-w-xl mx-auto">{lastRound ? `${battle.opponent_name} just dropped — clap back hard.` : "Set the tone. Make 'em remember the first bar."}</p>
 <button data-testid="step-mic-btn" onClick={() => setPhase("spitting")} className="bg-yt-red text-white font-display text-2xl tracking-wider px-10 py-4 hover:bg-yt-yellow hover:text-black shadow-brutal shadow-brutal-hover">▶ STEP TO THE MIC</button>
 <div className="mt-4 font-mono text-xs text-yt-muted">auto-starts in {introLeft}s</div>
 </div>
 )}

 {phase === "spitting" && (
 <div className="bg-yt-surface border border-yt-border p-6">
 <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
 <div className="font-display text-2xl tracking-tight">DROP YOUR BARS</div>
 <div className="flex gap-2">
 {mic.supported ? (
 <button data-testid="voice-toggle-btn" onClick={mic.toggle} disabled={mic.state === "transcribing"}
 className={`px-4 py-2 border font-mono text-xs tracking-widest transition-colors ${
 mic.state === "recording" ? "border-yt-red text-yt-red animate-pulse-red" :
 mic.state === "transcribing" ? "border-yt-yellow text-yt-yellow" :
 "border-white hover:border-yt-yellow hover:text-yt-yellow"}`}>
 {mic.state === "recording" ? "● REC · TAP TO STOP" :
 mic.state === "transcribing" ? "TRANSCRIBING..." :
 "SPIT (WHISPER AI)"}
 </button>
 ) : (
 <div className="px-4 py-2 border border-yt-muted text-yt-muted font-mono text-xs tracking-widest cursor-not-allowed">
 MIC NOT SUPPORTED
 </div>
 )}
 <button data-testid="clear-verse-btn" type="button" onClick={() => setVerse("")}
 className="px-4 py-2 border border-yt-border text-yt-muted font-mono text-xs tracking-widest hover:border-yt-red hover:text-yt-red">
 ✕ CLEAR
 </button>
 </div>
 </div>
 {mic.status && (
 <div data-testid="voice-status" className={`mb-2 font-mono text-xs px-3 py-2 border ${
 mic.state === "recording" ? "border-yt-red text-yt-red bg-yt-red/5" :
 "border-yt-yellow text-yt-yellow bg-yt-yellow/5"}`}>
 {mic.status}
 </div>
 )}
 <textarea
 data-testid="verse-input"
 value={verse}
 onChange={e => setVerse(e.target.value)}
 placeholder={`Spit your bars, ${user.rapper_name}. Type them, or tap SPIT to record. Whisper AI transcribes accurately. 8-12 lines hit hardest.`}
 rows={8} autoFocus
 className="w-full bg-black border border-yt-border p-4 font-mono text-white focus:border-yt-yellow focus:outline-none"
 />
 <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
 <div className="font-mono text-[10px] text-yt-muted">{verse.length} CHARS · {verse.split(/\n/).filter(l => l.trim()).length} BARS</div>
 <button data-testid="submit-verse-btn" onClick={submit} disabled={verse.length < 10}
 className="bg-yt-red text-white font-display text-lg tracking-wider px-8 py-3 hover:bg-yt-yellow hover:text-black disabled:opacity-40 shadow-brutal shadow-brutal-hover">
 DROP THE BAR ▶
 </button>
 </div>
 {err && <div data-testid="submit-error" className="mt-3 font-mono text-xs text-yt-red border border-yt-red/40 p-3">{err}</div>}
 </div>
 )}

 {/* ====== AUDIO PERFORMANCE PANEL (gen_user / play_user / gen_opp / play_opp) ====== */}
 {inAudioPhase && (
 <div className="bg-yt-surface border border-yt-border p-6" data-testid="performance-panel">
 <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
 <div className="font-display text-2xl tracking-tight">
 {phase === "gen_user" && "COOKING YOUR RAP AUDIO"}
 {phase === "gen_user_lipsync" && "SYNCING YOUR FACE + VOICE"}
 {phase === "play_user" && `▶ ${battle.rapper_name} — LIVE PERFORMANCE`}
 {phase === "gen_opp" && ` ${battle.opponent_name} IS LOADING THEIR COMEBACK`}
 {phase === "gen_opp_lipsync" && ` SYNCING ${battle.opponent_name?.toUpperCase()} FACE + VOICE`}
 {phase === "play_opp" && `▶ ${battle.opponent_name} — CLAPPING BACK`}
 </div>
 <div className="font-mono text-xs text-yt-yellow tracking-widest">SUNO · V4.5 · REPLICATE</div>
 </div>

 {/* Progress bar during gen (both audio and lipsync) */}
 {(phase === "gen_user" || phase === "gen_opp" || phase === "gen_user_lipsync" || phase === "gen_opp_lipsync") && (
 <>
 <div className="h-2 bg-black border border-yt-border overflow-hidden mb-2">
 <div className={`h-full transition-all ${phase.endsWith("_lipsync") ? "bg-yt-red" : "bg-yt-yellow"}`}
 style={{ width: `${genProgress}%` }} />
 </div>
 <div className="font-mono text-xs text-yt-muted mb-3">{audioStatus} · {Math.round(genProgress)}%</div>
 {phase.endsWith("_lipsync") && (
 <div className="font-mono text-[10px] text-yt-red mt-2">
 Waiting for face-animation to sync with the voice — they&apos;ll fire together. 30-90s typical.
 </div>
 )}
 </>
 )}

 {/* Audio player during play phases */}
 {phase === "play_user" && audioUserUrl && (
 <div className="bg-black border border-yt-yellow p-4 mb-3">
 <div className="font-mono text-[10px] text-yt-yellow tracking-widest mb-2">● NOW PLAYING · {battle.rapper_name?.toUpperCase()}</div>
 <audio
 data-testid="audio-user-live"
 key={`u-${audioUserUrl}`}
 src={audioUserUrl}
 autoPlay
 controls
 onEnded={advanceToOpponent}
 onError={() => { setAudioStatus("⚠ Playback error — advancing."); advanceToOpponent(); }}
 onTimeUpdate={(e) => { if (e.currentTarget.currentTime > 45) advanceToOpponent(); }}
 className="w-full"
 />
 <button data-testid="skip-user-audio" onClick={advanceToOpponent}
 className="mt-3 w-full bg-yt-yellow text-black font-display text-base tracking-widest py-3 hover:bg-white shadow-brutal">
 ▶▶ SKIP TO OPPONENT
 </button>
 </div>
 )}
 {phase === "play_opp" && audioOppUrl && (
 <div className="bg-black border border-yt-red p-4 mb-3">
 <div className="font-mono text-[10px] text-yt-red tracking-widest mb-2">● NOW PLAYING · {battle.opponent_name?.toUpperCase()}</div>
 <audio
 data-testid="audio-opp-live"
 key={`o-${audioOppUrl}`}
 src={audioOppUrl}
 autoPlay
 controls
 onEnded={revealVerdict}
 onError={() => { setAudioStatus("⚠ Playback error — advancing to verdict."); revealVerdict(); }}
 onLoadedMetadata={(e) => setStageAudioDur(e.currentTarget.duration || 0)}
 onTimeUpdate={(e) => {
 setStageAudioTime(e.currentTarget.currentTime || 0);
 if (e.currentTarget.currentTime > 45) revealVerdict();
 }}
 className="w-full"
 />
 <button data-testid="skip-opp-audio" onClick={revealVerdict}
 className="mt-3 w-full bg-yt-red text-white font-display text-base tracking-widest py-3 hover:bg-yt-yellow hover:text-black shadow-brutal">
 ▶▶ SKIP TO VERDICT
 </button>
 </div>
 )}
 {/* Silent rebuttal fallback: if BOTH Suno AND TTS failed, keep the verse
 readable in a modest text card + manual continue button so the flow
 is never stuck. In practice this rarely fires since TTS is fast + reliable. */}
 {phase === "play_opp" && !audioOppUrl && lastRound?.opponent_verse && (
 <div className="bg-black border border-yt-red p-4 mb-3" data-testid="opp-silent-fallback">
 <div className="font-mono text-[10px] text-yt-red tracking-widest mb-2">
 ⚠ VOICE UNAVAILABLE · {battle.opponent_name?.toUpperCase()}&apos;S BARS
 </div>
 <pre className="font-mono text-sm text-white whitespace-pre-wrap leading-relaxed mb-3">
 {lastRound.opponent_verse}
 </pre>
 <button data-testid="continue-to-verdict-btn" onClick={revealVerdict}
 className="w-full bg-yt-red text-white font-display text-lg tracking-wider py-3 hover:bg-yt-yellow hover:text-black shadow-brutal shadow-brutal-hover">
 ▶ CONTINUE TO VERDICT
 </button>
 </div>
 )}

 {/* Lyrics scrolling */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="bg-black/60 border border-yt-border p-4">
 <div className="font-mono text-[10px] text-yt-yellow tracking-widest mb-2">YOUR BARS</div>
 <pre className="font-mono text-sm text-white/90 whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto">
 {(phase === "gen_user" || phase === "play_user")
 ? (lastRound?.user_verse || verse)
 : (lastRound?.user_verse || "")}
 </pre>
 </div>
 <div className="bg-black/60 border border-yt-border p-4">
 <div className="font-mono text-[10px] text-yt-red tracking-widest mb-2">{battle.opponent_name?.toUpperCase()} BARS</div>
 <pre className="font-mono text-sm text-white/90 whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto">
 {(phase === "gen_opp" || phase === "play_opp")
 ? (lastRound?.opponent_verse || "Loading...")
 : (phase === "play_user" ? "Waiting for your turn to end..." : "")}
 </pre>
 </div>
 </div>
 </div>
 )}

 {/* ====== DONE: FINAL VERDICT + BACK TO LOBBY ====== */}
 {phase === "done" && (
 <div className="border border-yt-border bg-yt-surface" data-testid="battle-done-panel">
 <div className="border-b border-yt-border px-6 py-6 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-2"> FINAL VERDICT</div>
 <div className={`font-display text-6xl md:text-8xl tracking-tight leading-none mb-3 ${
 battle.winner === "user" ? "text-yt-yellow" :
 battle.winner === "opponent" ? "text-yt-red" : "text-white"}`}>
 {battle.status === "abandoned" ? "CLOSED · UNFINISHED" : battle.winner === "user" ? "★ VICTORY" :
 battle.winner === "opponent" ? "DEFEATED" : "DRAW"}
 </div>
 <div className="font-mono text-sm text-yt-muted tracking-widest">
 {battle.rapper_name?.toUpperCase()} <span className="text-yt-yellow mx-2">{userRoundsWon}</span>
 <span className="text-yt-muted">—</span>
 <span className="text-yt-red mx-2">{oppRoundsWon}</span> {battle.opponent_name?.toUpperCase()}
 </div>
 {/* Lipsync generation indicator — head-to-head face videos take ~30-60s */}
 {(() => {
 const hasLip = lastRound?.lipsync_user || lastRound?.lipsync_opponent;
 return (
 <div className="mt-4 font-mono text-[10px] tracking-[0.3em] inline-block px-3 py-1 border"
 data-testid="lipsync-status"
 style={{ borderColor: hasLip ? "#FFD700" : "#666", color: hasLip ? "#FFD700" : "#999" }}>
 {hasLip ? "LIPSYNC READY · SCROLL UP" : "GENERATING HEAD-TO-HEAD LIPSYNC..."}
 </div>
 );
 })()}
 </div>
 <div className="p-6 flex flex-col md:flex-row gap-4 items-center justify-center flex-wrap">
 <Link
 to="/battles/new"
 data-testid="battle-again-btn"
 className="bg-yt-red text-white font-display text-xl tracking-wider px-8 py-3 hover:bg-yt-yellow hover:text-black shadow-brutal shadow-brutal-hover"
 >
 ▶ BATTLE AGAIN
 </Link>
 <Link
 to="/"
 data-testid="back-to-lobby-btn"
 className="border border-yt-yellow text-yt-yellow font-display text-xl tracking-wider px-8 py-3 hover:bg-yt-yellow hover:text-black shadow-brutal"
 >
 ◀ BACK TO LOBBY
 </Link>
 <ShareBattleButton highlightEndpoint={`/battles/${battle.id}/highlight-mp4`} />
 </div>
 </div>
 )}

 {/* ====== REVEAL: JUDGE SPEAKS + SCORES ====== */}
 {(phase === "reveal" || phase === "judge_speak") && lastRound && (
 <div className="border border-yt-border bg-yt-surface" data-testid="reveal-panel">
 <div className="border-b border-yt-border px-6 py-3 flex justify-between items-center flex-wrap gap-2">
 <div className="font-display text-xl tracking-tight">ROUND {lastRound.round} · VERDICT</div>
 <div className={`font-mono text-xs tracking-widest ${lastRound.winner === "user" ? "text-yt-judge" : "text-yt-red"}`}>
 {lastRound.winner === "user" ? "★ YOU TOOK IT" : "OPPONENT EDGED IT"}
 </div>
 </div>

 {/* Judge audio player */}
 <div className="border-b border-yt-border bg-black p-4 scanlines">
 <div className="font-mono text-[10px] tracking-widest text-yt-red mb-2"> JUDGE DEGAGNE · SPEAKING</div>
 {judgeAudioUrl ? (
 <audio
 data-testid="audio-judge"
 key={`j-${judgeAudioUrl}`}
 src={judgeAudioUrl}
 autoPlay
 controls
 className="w-full"
 />
 ) : (
 <div className="font-mono text-xs text-yt-muted py-2">⏳ Judge is clearing his throat...</div>
 )}
 <div className="font-mono text-sm text-yt-judge mt-3 min-h-[3em] leading-relaxed">
 {typedVerdict || (lastRound.judge_user?.verdict || "").slice(0, 1)}
 <span className="cursor-blink"></span>
 </div>
 </div>

 {/* Audio playback strip (replay both raps) */}
 <div className="border-b border-yt-border bg-black p-4">
 <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2"> REPLAY THE PERFORMANCES</div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <div>
 <div className="font-display text-sm mb-1">{battle.rapper_name}</div>
 {audioUserUrl ? (
 <audio data-testid="audio-user" controls src={audioUserUrl} className="w-full" />
 ) : (
 <div className="font-mono text-xs text-yt-muted py-3 border border-yt-border px-3">— No audio —</div>
 )}
 </div>
 <div>
 <div className="font-display text-sm mb-1">{battle.opponent_name}</div>
 {audioOppUrl ? (
 <audio data-testid="audio-opp" controls src={audioOppUrl} className="w-full" />
 ) : (
 <div className="font-mono text-xs text-yt-muted py-3 border border-yt-border px-3">— No audio —</div>
 )}
 </div>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
 <VerseBlock title={battle.rapper_name} verse={lastRound.user_verse} judge={lastRound.judge_user} />
 <VerseBlock title={battle.opponent_name} verse={lastRound.opponent_verse} judge={lastRound.judge_opponent} alt />
 </div>
 </div>
 )}
 </div>

 {/* Fight history */}
 {battle.rounds.length > (inRevealPhase ? 1 : 0) && (
 <div className="max-w-7xl mx-auto px-6 md:px-10 pb-12">
 <div className="font-display text-2xl tracking-tight mb-4 text-yt-muted">FIGHT HISTORY</div>
 <div className="space-y-4">
 {[...battle.rounds].reverse().slice(inRevealPhase ? 1 : 0).map(r => (
 <div key={r.round} className="border border-yt-border bg-yt-surface">
 <div className="border-b border-yt-border px-6 py-3 flex justify-between items-center">
 <div className="font-display text-xl tracking-tight">ROUND {r.round}</div>
 <div className={`font-mono text-xs tracking-widest ${r.winner === "user" ? "text-yt-judge" : "text-yt-red"}`}>
 {r.winner === "user" ? "YOU TOOK IT" : "OPPONENT EDGED IT"}
 </div>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
 <VerseBlock title={battle.rapper_name} verse={r.user_verse} judge={r.judge_user} />
 <VerseBlock title={battle.opponent_name} verse={r.opponent_verse} judge={r.judge_opponent} alt />
 </div>
 <div className="bg-[#0A0A0A] border-t border-yt-red px-6 py-4 scanlines">
 <div className="font-mono text-[10px] text-yt-red tracking-widest mb-1">[ JUDGE DEGAGNE · VERDICT ]</div>
 <div className="font-mono text-sm text-yt-judge">{r.judge_user?.verdict}</div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 );
}

/* ============================================================
 STAGE FIGURE
 ============================================================ */
function StageFigure({ side, name, avatar, portrait, active, rapping }) {
 const flip = side === "right" ? "scale-x-[-1]" : "";
 // Idle-bob when it's this rapper's "corner" but not yet spitting.
 // Mic-raise animation drives the emoji mic to the mouth when they spit.
 const bob = active && !rapping ? "animate-idle-bob" : "";
 return (
 <div className={`relative ${active ? "scale-105 z-10" : "scale-95 opacity-75"} transition-all duration-700`}>
 <div className={`relative ${active ? "ring-4 ring-yt-yellow" : "ring-2 ring-white/20"} bg-yt-surface overflow-hidden shadow-2xl mx-auto ${bob}`}
 style={{ width: "min(280px, 26vw)", height: "min(360px, 34vw)" }}>
 {portrait ? (
 <img src={portrait} alt={name} className={`w-full h-full object-cover ${flip}`} />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yt-surface to-black font-display text-4xl text-yt-yellow">{(name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 {rapping && (
 <>
 <div className="absolute inset-0 ring-4 ring-yt-yellow animate-pulse pointer-events-none" />
 {/* Mic starts low-right and animates up to mouth when rapping */}
 <div
 className={`absolute text-4xl drop-shadow-[0_0_8px_rgba(255,215,0,0.9)] animate-mic-raise`}
 style={{
 bottom: side === "left" ? "45%" : "45%",
 right: side === "left" ? "22%" : "auto",
 left: side === "right" ? "22%" : "auto",
 }}
 ></div>
 <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0.5 p-2 bg-black/40">
 {[1,2,3,4,5,6,7,8,9].map(i => (
 <div key={i} className="w-1 bg-yt-yellow" style={{ height: `${8 + Math.random()*22}px`, animation: `pulse-red 0.${3+i}s ease-in-out infinite alternate` }} />
 ))}
 </div>
 </>
 )}
 </div>
 </div>
 );
}

function BroadcastTimer({ label, value, active, side, testId }) {
 return (
 <div data-testid={testId} className={`bg-black/90 border-2 ${active ? "border-yt-red" : "border-white/30"} backdrop-blur-sm py-2 px-4 ${side === "right" ? "text-right" : ""}`}>
 <div className="font-display text-base tracking-wider text-white">{label}</div>
 <div className={`font-display text-3xl md:text-5xl leading-none ${active ? "text-yt-red" : "text-white/40"}`}>{value}</div>
 </div>
 );
}

function NameTag({ side, name, streaming, streamColor, testId }) {
 return (
 <div data-testid={testId} className={`bg-black/90 border border-white/20 backdrop-blur-sm flex items-center gap-2 px-3 py-2 ${side === "right" ? "justify-end" : ""}`}>
 {streaming && side === "left" && (
 <span className={`inline-block px-2 py-0.5 ${streamColor === "red" ? "bg-yt-red" : "bg-yt-judge"} text-black font-mono text-[9px] tracking-widest`}>
 ● LIVE
 </span>
 )}
 <span className="font-display text-lg md:text-xl tracking-tight uppercase">{name}</span>
 {streaming && side === "right" && (
 <span className={`inline-block px-2 py-0.5 ${streamColor === "red" ? "bg-yt-red" : "bg-yt-judge"} text-black font-mono text-[9px] tracking-widest`}>
 ● LIVE
 </span>
 )}
 </div>
 );
}

function VerseBlock({ title, verse, judge, alt }) {
 return (
 <div className={`p-6 ${alt ? "border-l border-yt-border" : ""}`}>
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted mb-3">{title.toUpperCase()}</div>
 <pre className="font-mono text-sm text-white/90 whitespace-pre-wrap leading-relaxed">{verse}</pre>
 <div className="mt-4 grid grid-cols-5 gap-1 text-center">
 {[["FLO", judge?.flow],["RHY", judge?.rhyme],["TPC", judge?.topic],["PCH", judge?.punchlines],["CRD", judge?.crowd]].map(([k, v]) => (
 <div key={k} className="border border-yt-border bg-black py-2">
 <div className="font-display text-xl text-yt-yellow leading-none">{v ?? 0}</div>
 <div className="font-mono text-[8px] text-yt-muted tracking-widest">{k}</div>
 </div>
 ))}
 </div>
 <div className="font-mono text-[10px] text-yt-judge mt-2">TOTAL · {judge?.total ?? 0}/100</div>
 </div>
 );
}
